import React, { useState, useEffect } from "react";
import { 
  FaGraduationCap, 
  FaCheck, 
  FaHeart, 
  FaFemale, 
  FaLeaf, 
  FaBullseye,
  FaBalanceScale,
  FaStethoscope,
  FaArrowRight
} from 'react-icons/fa';

// Make sure this path is correct for your project structure.
import profileImage from '../img/image16.png';

// --- TEXT CONTENT ---
const shortBio = `I'm Dietitian Karpagam, a practical and service-oriented nutrition professional with over 6 years of experience in clinical and lifestyle nutrition. After completing my M.Sc. in Clinical Nutrition and working at Apollo Hospitals, Chennai, I've helped hundreds of individuals transform their health through science-based, realistic diet plans.`;

const longBio = `I'm Karpagam, Clinical dietitian—a practical, empathetic, and service-oriented nutrition professional with over 6 years of experience helping people achieve sustainable health transformations through the most powerful medicine: food.

After completing my M.Sc. in Clinical Nutrition from Sri Ramachandra Institute of Higher Education and Research, I began my career at Apollo Hospitals, Greams Road, Chennai, where I gained rich hands-on experience managing diverse clinical and lifestyle cases.

Over the years, I've helped individuals reclaim their health—from a 55-year-old prediabetic woman who has maintained normal A1c levels for over three years, to busy working professionals who achieved healthy weight loss without fad or restrictive diets.

At Karps Wellness, I bring science-backed nutrition, practical meal planning, and lifestyle guidance together to create customised diet programs that are easy to follow, enjoyable, and result-oriented.

My approach is simple: No extremes. No deprivation. Just conscious eating, consistent habits, and smart nutrition choices.

Beyond consultations, I've also been part of several nutrition awareness programs, motivational talks, and health events, where I share one core message: wellness doesn't begin in the lab or the gym—it begins on your plate.

Whether it's for weight loss, diabetes management, PCOS, chronic disease management, or simply eating better—I'm here to help you make nutrition a part of your lifestyle, not a temporary fix.

Because true wellness starts from within, and I'm here to guide you, one meal, one habit, and one conscious step at a time.`;

const specializations = [
  { icon: FaBalanceScale, title: 'Weight Management', desc: 'Healthy weight loss and weight gain plans without fad diets.' },
  { icon: FaStethoscope, title: 'Diabetes Care', desc: 'Nutrition support for Type 1 & 2 diabetes, Prediabetes & GDM.' },
  { icon: FaFemale, title: 'Women\'s Hormonal Health', desc: 'Diet plans for PCOS/PCOD, fertility, pregnancy & postpartum wellness.' },
  { icon: FaHeart, title: 'Heart Health', desc: 'Heart-friendly nutrition for cholesterol, BP & overall cardiac wellness.' },
  { icon: FaLeaf, title: 'Lifestyle Nutrition', desc: 'Everyday healthy habits for better energy, gut health & immunity.' },
  { icon: FaBullseye, title: 'Clinical Nutrition', desc: 'Therapeutic diets for thyroid, fatty liver, hypertension & kidney issues.' }
];

// --- MAIN COMPONENT ---
const AboutUs = ({ onOpenEnquiryForm }) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showFullBio, setShowFullBio] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Breakpoints
  const isTinyMobile = windowWidth <= 390; // iPhone SE / Small screens
  const isMobile = windowWidth <= 768;     // Standard phones
  const isTablet = windowWidth <= 1024;    // iPads

  // --- DYNAMIC RESPONSIVE STYLES ---
  const styles = {
    pageContainer: {
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: '#374151',
      overflowX: 'hidden', // Prevents any horizontal scrolling on mobile
      boxSizing: 'border-box',
    },
    
    // Navigation
    nav: {
      position: 'fixed', top: 0, left: 0, right: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(8px)',
      borderBottom: '1px solid #e5e7eb',
      zIndex: 50,
    },
    navContainer: {
      maxWidth: '1200px', margin: '0 auto',
      padding: isTinyMobile ? '12px 16px' : '16px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    },
    logoContainer: {
      display: 'flex', alignItems: 'center', gap: '10px',
    },
    logoCircle: {
      width: isTinyMobile ? '32px' : '40px', 
      height: isTinyMobile ? '32px' : '40px',
      backgroundColor: '#0d9488', borderRadius: '6px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#ffffff', fontWeight: 'bold', 
      fontSize: isTinyMobile ? '16px' : '20px',
    },
    logoText: {
      fontSize: isTinyMobile ? '16px' : '20px', 
      fontWeight: '700', color: '#111827',
    },
    navLinks: {
      display: isMobile ? 'none' : 'flex', gap: '32px',
    },
    navLink: {
      textDecoration: 'none', color: '#4b5563', fontSize: '15px', fontWeight: '500', transition: 'color 0.2s', cursor: 'pointer',
    },
    navButton: {
      backgroundColor: '#0d9488', color: '#ffffff',
      padding: isTinyMobile ? '8px 16px' : '10px 20px', 
      borderRadius: '6px', border: 'none', 
      fontWeight: '600', fontSize: isTinyMobile ? '13px' : '14px',
      cursor: 'pointer', transition: 'background-color 0.2s',
      whiteSpace: 'nowrap',
    },
    
    // Hero Section
    heroSection: {
      paddingTop: isMobile ? '100px' : '140px', 
      paddingBottom: isMobile ? '50px' : '80px',
      paddingLeft: 'clamp(16px, 5vw, 24px)', 
      paddingRight: 'clamp(16px, 5vw, 24px)',
    },
    heroContainer: {
      maxWidth: '1200px', margin: '0 auto',
    },
    heroGrid: {
      display: 'grid', 
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
      gap: 'clamp(32px, 6vw, 64px)', 
      alignItems: 'center',
    },
    
    // Image & Badges (Highly fluid for all phones)
    imageColumnWrapper: {
      position: 'relative',
      width: '100%',
      maxWidth: '450px',
      margin: '0 auto',
      padding: 'clamp(10px, 4vw, 20px)', // Ensures badges never touch screen edges
      display: 'flex', justifyContent: 'center',
    },
    mainImageContainer: {
      width: '100%',
      aspectRatio: '4/5', // Auto-scales height based on width perfectly
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 12px 35px rgba(0, 0, 0, 0.08)',
      border: '1px solid #f3f4f6',
      backgroundColor: '#f9fafb',
    },
    mainImage: {
      width: '100%', height: '100%', objectFit: 'cover', display: 'block',
    },
    floatingBadge1: {
      position: 'absolute',
      bottom: isTinyMobile ? '15px' : '30px',
      left: '0px',
      backgroundColor: '#ffffff',
      borderRadius: '10px',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
      padding: isTinyMobile ? '10px 12px' : '14px 18px',
      zIndex: 20,
      display: 'flex', alignItems: 'center', gap: '12px',
      border: '1px solid #f3f4f6',
      transform: isTinyMobile ? 'scale(0.85)' : 'scale(1)', // Shrinks on small phones
      transformOrigin: 'bottom left',
    },
    badgeIcon: {
      width: '36px', height: '36px',
      backgroundColor: '#f0fdfa', borderRadius: '8px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '18px', color: '#0d9488',
    },
    badgeTitle: {
      fontWeight: '700', color: '#111827', fontSize: '13px',
    },
    badgeSubtitle: {
      fontSize: '11px', color: '#6b7280', marginTop: '2px', fontWeight: '500',
    },
    floatingBadge2: {
      position: 'absolute',
      top: isTinyMobile ? '15px' : '30px',
      right: '0px',
      backgroundColor: '#ffffff',
      borderRadius: '10px',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
      padding: isTinyMobile ? '12px' : '16px',
      zIndex: 20,
      textAlign: 'center',
      border: '1px solid #f3f4f6',
      transform: isTinyMobile ? 'scale(0.85)' : 'scale(1)', // Shrinks on small phones
      transformOrigin: 'top right',
    },
    badgeNumber: {
      fontSize: '22px', fontWeight: '800', color: '#0d9488', lineHeight: '1',
    },
    badgeLabel: {
      fontSize: '11px', color: '#4b5563', fontWeight: '600', marginTop: '6px',
    },
    
    // Text Content
    textColumn: {
      display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 3vw, 24px)',
    },
    tagline: {
      color: '#0d9488', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1.5px',
    },
    mainTitle: {
      fontSize: 'clamp(28px, 6vw, 42px)', // Scales dynamically on every screen size
      fontWeight: '800', color: '#111827', lineHeight: '1.2', margin: '0 0 8px 0',
    },
    bioText: {
      fontSize: 'clamp(15px, 3vw, 17px)', color: '#4b5563', lineHeight: '1.7', margin: 0,
    },
    
    // Key Points Grid
    keyPointsGrid: {
      display: 'grid', 
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
      gap: '16px',
      paddingTop: '8px', paddingBottom: '8px',
    },
    keyPoint: {
      display: 'flex', alignItems: 'center', gap: '12px',
      backgroundColor: isMobile ? '#f9fafb' : 'transparent',
      padding: isMobile ? '12px' : '0',
      borderRadius: isMobile ? '8px' : '0',
    },
    keyPointIcon: {
      color: '#0d9488', fontSize: '16px', flexShrink: 0,
    },
    keyPointTitle: {
      fontWeight: '600', color: '#374151', fontSize: '15px',
    },
    
    // CTA Button
    ctaButton: {
      backgroundColor: '#0d9488', color: '#ffffff',
      padding: '16px 24px', borderRadius: '8px', border: 'none',
      fontWeight: '600', fontSize: '15px', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
      transition: 'all 0.2s ease',
      width: isMobile ? '100%' : 'max-content', // Full width on mobile
      marginTop: '10px',
    },
    
    // Full Bio Section
    fullBioSection: {
      padding: 'clamp(50px, 8vw, 80px) clamp(16px, 5vw, 24px)', 
      backgroundColor: '#f9fafb',
      borderTop: '1px solid #e5e7eb',
    },
    fullBioContainer: {
      maxWidth: '800px', margin: '0 auto',
    },
    fullBioTitle: {
      fontSize: 'clamp(24px, 5vw, 32px)', fontWeight: '800', color: '#111827', marginBottom: '24px', textAlign: isMobile ? 'left' : 'center',
    },
    fullBioParagraph: {
      fontSize: 'clamp(15px, 3vw, 17px)', color: '#4b5563', lineHeight: '1.8', marginBottom: '20px',
    },
    
    // Specializations Section
    specializationsSection: {
      padding: 'clamp(60px, 8vw, 100px) clamp(16px, 5vw, 24px)', 
      backgroundColor: '#ffffff',
    },
    sectionHeader: {
      textAlign: 'center', marginBottom: 'clamp(32px, 6vw, 48px)',
    },
    sectionTitle: {
      fontSize: 'clamp(26px, 6vw, 36px)', fontWeight: '800', color: '#111827', margin: '0 0 12px 0',
    },
    sectionSubtitle: {
      color: '#6b7280', fontSize: 'clamp(15px, 3vw, 17px)', margin: 0,
    },
    specializationsGrid: {
      display: 'grid', 
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', 
      gap: 'clamp(16px, 4vw, 32px)', 
      maxWidth: '1200px', margin: '0 auto',
    },
    specializationCard: {
      backgroundColor: '#ffffff',
      padding: 'clamp(24px, 5vw, 32px)',
      borderRadius: '16px',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    },
    cardIcon: {
      fontSize: '32px', color: '#0d9488', marginBottom: '20px',
    },
    cardTitle: {
      fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '12px',
    },
    cardDesc: {
      color: '#4b5563', fontSize: '15px', lineHeight: '1.6', margin: 0,
    },
  };

  const handleLearnMore = () => {
    setShowFullBio(!showFullBio);
    if (!showFullBio) {
      setTimeout(() => {
        document.querySelector('#full-bio')?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  };

  // Helper render for Image to ensure HTML matches exactly on mobile & desktop
  const renderImageSection = () => (
    <div style={styles.imageColumnWrapper}>
      <div style={styles.mainImageContainer}>
        <img 
          src={profileImage} 
          alt="Dietitian Karpagam" 
          style={styles.mainImage}
          onError={(e) => { e.target.style.backgroundColor = '#f3f4f6'; }}
        />
      </div>

      <div style={styles.floatingBadge1}>
        <div style={styles.badgeIcon}><FaGraduationCap /></div>
        <div>
          <div style={styles.badgeTitle}>M.Sc Clinical Nutrition</div>
        </div>
      </div>

      <div style={styles.floatingBadge2}>
        <div style={styles.badgeNumber}>6+</div>
        <div style={styles.badgeLabel}>Years Experience</div>
      </div>
    </div>
  );

  return (
    <div style={styles.pageContainer}>
      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navContainer}>
          <div style={styles.logoContainer}>
            <div style={styles.logoCircle}>K</div>
            <span style={styles.logoText}>Karps Wellness</span>
          </div>
          <div style={styles.navLinks}>
            <a href="/" style={styles.navLink}>Home</a>
            <a href="#services" style={styles.navLink}>Services</a>
            <a href="#about" style={{...styles.navLink, color: '#0d9488', fontWeight: '600'}}>About</a>
            <a href="/contact" style={styles.navLink}>Contact</a>
          </div>
          <button style={styles.navButton} onClick={onOpenEnquiryForm}>
            Book Now
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={styles.heroSection}>
        <div style={styles.heroContainer}>
          <div style={styles.heroGrid}>
            
            {/* Show Image First on Mobile */}
            {isMobile && renderImageSection()}

            {/* Text Content */}
            <div style={styles.textColumn}>
              <span style={styles.tagline}>Professional Nutrition Expert</span>
              <h1 style={styles.mainTitle}>About Dietitian Karpagam</h1>
              
              <p style={styles.bioText}>{shortBio}</p>

              <div style={styles.keyPointsGrid}>
                {['Science-Based Evidence', 'Personalized Plans', 'Sustainable Results', 'Clinical Expertise'].map((point, idx) => (
                  <div key={idx} style={styles.keyPoint}>
                    <FaCheck style={styles.keyPointIcon} />
                    <span style={styles.keyPointTitle}>{point}</span>
                  </div>
                ))}
              </div>

              <button
                style={{ 
                  ...styles.ctaButton, 
                  ...(isButtonHovered ? { backgroundColor: '#0f766e', transform: 'translateY(-2px)' } : {}) 
                }}
                onClick={handleLearnMore}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
              >
                {showFullBio ? 'Read Less' : 'Learn More About My Journey'}
                <FaArrowRight style={{ transition: 'transform 0.3s', transform: showFullBio ? 'rotate(-90deg)' : 'rotate(0)' }}/>
              </button>
            </div>

            {/* Show Image Second on Desktop/Tablet */}
            {!isMobile && renderImageSection()}
            
          </div>
        </div>
      </section>

      {/* Full Bio Section */}
      {showFullBio && (
        <section id="full-bio" style={styles.fullBioSection}>
          <div style={styles.fullBioContainer}>
            <h2 style={styles.fullBioTitle}>My Complete Journey</h2>
            {longBio.split('\n\n').map((paragraph, index) => (
              <p key={index} style={styles.fullBioParagraph}>{paragraph.trim()}</p>
            ))}
          </div>
        </section>
      )}

      {/* Specializations Section */}
      <section id="specializations" style={styles.specializationsSection}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Areas of Expertise</h2>
          <p style={styles.sectionSubtitle}>Comprehensive care tailored for your unique health needs.</p>
        </div>
        
        <div style={styles.specializationsGrid}>
          {specializations.map((item, idx) => {
            const IconComponent = item.icon;
            const isHovered = hoveredCard === idx;
            return (
              <div
                key={idx}
                style={{ 
                  ...styles.specializationCard, 
                  ...(isHovered ? { boxShadow: '0 12px 24px rgba(0,0,0,0.08)', borderColor: '#0d9488', transform: 'translateY(-4px)' } : {}) 
                }}
                onMouseEnter={() => setHoveredCard(idx)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <IconComponent style={styles.cardIcon} />
                <h3 style={styles.cardTitle}>{item.title}</h3>
                <p style={styles.cardDesc}>{item.desc}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
